import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { planAPI } from '../lib/api';
import { Sparkles, Lock } from 'lucide-react';

/**
 * Displays a compact banner showing current usage vs. plan limit for a specific resource.
 * Props:
 *  - resource: "products" | "landing_pages" | "checkout_forms"
 *  - label: display label e.g. "products"
 */
export default function PlanLimitBanner({ resource, label }) {
  const [usage, setUsage] = useState(null);

  useEffect(() => {
    let alive = true;
    planAPI.usage()
      .then(res => { if (alive) setUsage(res.data); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  if (!usage) return null;
  const r = usage.resources?.[resource];
  if (!r) return null;

  const plan = usage.plan;
  const limit = r.limit; // null = unlimited
  const used = r.used;
  const atLimit = r.at_limit;
  const isUnlimited = limit === null;

  if (isUnlimited) return null; // no need to show for Premium

  const pct = limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
  const nearLimit = pct >= 80;

  return (
    <div
      className="mb-4 rounded-2xl px-4 py-3 flex items-center gap-3 flex-wrap"
      style={{
        border: atLimit ? '1.5px solid #FF9A00' : '1.5px solid #195259',
        backgroundColor: atLimit ? '#FFF6E6' : '#FFFFFF',
      }}
      data-testid={`plan-limit-banner-${resource}`}
    >
      <div className="flex items-center gap-2 min-w-0 flex-1">
        {atLimit ? (
          <Lock className="h-4 w-4 flex-shrink-0" style={{ color: '#FF9A00' }} />
        ) : (
          <Sparkles className="h-4 w-4 flex-shrink-0" style={{ color: '#195259' }} />
        )}
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold" style={{ color: '#195259' }}>
            {used} / {limit} {label} used
            <span
              className="ml-2 text-xs font-medium px-2 py-0.5 rounded-full"
              style={{ backgroundColor: '#195259', color: '#FFFFFF' }}
            >
              {plan.toUpperCase()}
            </span>
          </p>
          <div className="mt-1.5 h-1.5 w-full rounded-full overflow-hidden" style={{ backgroundColor: '#E5E7EB' }}>
            <div
              className="h-full rounded-full transition-all"
              style={{
                width: `${pct}%`,
                backgroundColor: atLimit ? '#FF9A00' : (nearLimit ? '#FF9A00' : '#195259'),
              }}
            />
          </div>
        </div>
      </div>
      {(atLimit || nearLimit) && (
        <Link
          to="/app/plan"
          className="text-xs font-bold px-3 py-1.5 rounded-lg whitespace-nowrap transition-colors"
          style={{ backgroundColor: '#FF9A00', color: '#195259' }}
          data-testid={`plan-limit-upgrade-${resource}`}
        >
          Upgrade →
        </Link>
      )}
    </div>
  );
}
